import type { Catalogue } from './es';

/**
 * English.
 *
 * Typed as `Catalogue`, so this file cannot compile with a key missing, a key
 * misspelled, or a function whose parameters drifted from the Spanish one.
 *
 * Length is a real constraint here, not a style note. Press Start 2P is a wide
 * face and every screen was laid out against the Spanish copy, so a translation
 * that is faithful but longer pushes a label past its row. Where the natural
 * English is too long the shorter true synonym wins — the audit's overflow
 * checks are what actually settle it, and they run in both languages.
 */
export const en: Catalogue = {
  common: {
    loading: 'LOADING...',
    offline: 'OFFLINE',
    couldNotLoad: 'COULD NOT LOAD',
    retry: 'RETRY',
    cancel: 'CANCEL',
    back: 'BACK',
    player: 'PLAYER',
    guest: 'GUEST',
    points: 'POINTS',
    moves: 'MOVES',
    maxTile: 'MAX TILE',
    maxCombo: 'MAX COMBO',
    matches: 'MATCHES',
    score: 'SCORE',
    date: 'DATE',
    rank: 'RANK',
    position: 'POS',
    name: 'NAME',
    playerColumn: 'PLAYER',
    winsLosses: 'W/L',
    max: 'MAX',
    modesMenu: 'MODE MENU',
    combo: (n: number) => `COMBO x${n}`,
    versusName: (name: string) => `VS ${name}`,
    ariaBack: 'Back',
    ariaClose: 'Dismiss notification',
    ariaNotifications: 'Notifications',
  },

  menu: {
    subtitle: 'BATTLE',
    play: 'PLAY',
    rankings: 'RANKINGS',
    history: 'HISTORY',
    settings: 'SETTINGS',
    online: 'ONLINE',
    offline: 'OFFLINE',
  },

  play: {
    singlePlayer: '◆ SINGLE PLAYER',
    multiplayer: '◆ MULTIPLAYER',
    modes: {
      matchTitle: 'FIND MATCH',
      matchHint: 'ONLINE RIVAL AT YOUR LEVEL',
      botTitle: 'VS BOT',
      botHint: 'INSTANT MATCH',
      privateTitle: 'PRIVATE MATCH',
      privateHint: 'CREATE OR JOIN WITH A CODE',
      timedTitle: '2 MINUTES',
      timedHint: 'BEST SCORE IN 2:00',
      endlessTitle: 'NO LIMIT',
      endlessHint: 'PERSONAL BEST · NOT RANKED',
    },
    // No possessive apostrophes: the curly one is not in the font subset and the
    // straight one only invites the confusion. Rephrased instead.
    chooseDifficulty: '◆ PICK YOUR RIVAL',
    difficulties: {
      easy: { title: 'EASY', hint: 'SLOW AND DISTRACTED' },
      medium: { title: 'MEDIUM', hint: 'PLAYS WELL · SLIPS SOMETIMES' },
      hard: { title: 'HARD', hint: 'THINKS TWO MOVES AHEAD' },
      insane: { title: 'INSANE', hint: 'NEVER SLIPS · FAST' },
    },
    difficultyFootnote: 'WITH NOBODY ONLINE, THE CPU STEPS IN',
    privateIntro: 'CREATE A ROOM AND SHARE THE CODE, OR TYPE THE ONE YOU WERE SENT.',
    createRoom: 'CREATE ROOM',
    enterCode: 'JOIN WITH CODE',
    codePrompt: 'TYPE THE CODE FROM YOUR FRIEND',
    codeIncomplete: 'COMPLETE THE CODE TO CONTINUE',
    join: 'JOIN',
    personalBest: (score: string) => `YOUR BEST: ${score}`,
  },

  matchmaking: {
    searching: 'SEARCHING',
    opponent: 'OPPONENT...',
    time: 'TIME',
    yourElo: 'YOUR ELO',
    unranked: 'UNRANKED',
    versus: 'VS',
    autoCancel: 'THE SEARCH CANCELS ITSELF WHEN IT RUNS OUT',
    ariaSearching: 'Searching for an opponent',
  },

  battle: {
    ready: 'READY',
    steady: 'STEADY',
    go: 'GO!',
    next: 'NEXT',
    combo: 'COMBO',
    round: 'ROUND 01',
    swipe: 'SWIPE',
    result: 'RESULT',
    victory: 'VICTORY',
    defeat: 'DEFEAT',
    draw: 'DRAW',
    rematch: 'REMATCH',
    powerActivated: 'POWER ACTIVATED',
    reconnecting: 'RECONNECTING...',
    keepingYourGame: 'KEEPING YOUR GAME',
    preparingBoard: 'PREPARING BOARD...',
    preparingBot: 'PREPARING BOT...',
    waitingOpponent: 'WAITING FOR RIVAL...',
    searchingOpponent: 'SEARCHING OPPONENT...',
    waiting: 'WAITING',
    shareCode: 'SHARE THIS CODE',
    tapInvite: 'TAP INVITE TO SHARE',
    invite: 'INVITE',
    room: 'ROOM',
    fifteenSecondWindow: '15 SECOND WINDOW',
    connectionRequired: 'CONNECTION REQUIRED',
    gameOver: 'GAME OVER',
    offlineNotSaved: 'OFFLINE · THIS RUN IS NOT SAVED',
    playAgain: 'PLAY AGAIN',
    preparing: 'PREPARING...',
    time: 'TIME',
    endless: 'NO LIMIT',
    timed: '2 MINUTES',
    swipeHint: (combo: number, energy: number) =>
      `SWIPE OR USE THE ARROWS\nMAX COMBO x${combo} · ENERGY ${energy}`,
    statusReady: 'READY',
    statusFight: 'FIGHT',
    statusAttack: 'ATTACK',
    statusHit: 'HIT!',
    statusKo: 'K.O.',
    statusLobby: 'LOBBY',
    statusWait: 'WAIT',
    statusOnline: 'ONLINE',
    statusBot: 'BOT',
    statusCpu: 'CPU',
  },

  rankings: {
    title: 'RANKINGS',
    boardElo: 'ELO',
    boardScore: 'SCORE',
    scopeGlobal: 'GLOBAL',
    scopeWeekly: 'WEEKLY',
    scopeDaily: 'TODAY',
    versus: 'VERSUS',
    all: 'ALL',
    worldRecord: 'WORLD RECORD',
    offlineMessage: 'OFFLINE\n\nCOULD NOT LOAD\nTHE BOARD.',
    emptyMessage: 'NO SCORES YET\n\nPLAY A MATCH\nTO APPEAR ON\nTHE BOARD.',
    ariaBoard: 'Board',
    ariaScope: 'Period',
    weeklyDelta: (delta: string) => `${delta} THIS WEEK`,
  },

  history: {
    title: 'HISTORY',
    offlineMessage: 'OFFLINE\n\nCOULD NOT LOAD\nTHE HISTORY.',
    emptyMessage: 'NO MATCHES YET\n\nPLAY ONE AND IT\nWILL SHOW UP HERE.',
    playedSolo: 'SOLO RUN',
    outcome: {
      win: 'VICTORY',
      loss: 'DEFEAT',
      draw: 'DRAW',
      abandoned: 'ABANDONED',
    },
    mode: {
      versus: 'VERSUS',
      bot: 'VS BOT',
      solo: '2 MINUTES',
      endless: 'NO LIMIT',
    },
  },

  profile: {
    title: 'PROFILE',
    bestScore: 'YOUR BEST SCORE',
    bestScorePublic: 'BEST SCORE',
    versusElo: 'VERSUS ELO',
    matches: 'MATCHES',
    winsLosses: 'W / L',
    bestStreak: 'BEST STREAK',
    achievements: 'AWARDS',
    viewAchievements: 'VIEW AWARDS',
    history: 'HISTORY',
    highScores: 'HIGH SCORES',
    offlineMessage: 'OFFLINE\n\nCOULD NOT LOAD\nYOUR PROFILE.',
    offlineMessagePublic: 'OFFLINE\n\nCOULD NOT LOAD\nTHE PROFILE.',
  },

  highScores: {
    title: 'HIGH SCORES',
    bestScore: 'YOUR BEST SCORE',
    bestScorePublic: 'BEST SCORE',
    offlineMessage: 'OFFLINE\n\nCOULD NOT LOAD\nYOUR RECORDS.',
    offlineMessagePublic: 'OFFLINE\n\nCOULD NOT LOAD\nTHE RECORDS.',
    emptyMessage: 'NO SOLO RUNS YET.\n\nPLAY ONE TO OPEN\nTHE TABLE.',
    emptyMessagePublic: 'THIS PLAYER HAS NO\nSOLO RUNS YET.',
  },

  achievements: {
    title: 'ACHIEVEMENTS',
    all: 'ALL',
    unlocked: 'UNLOCKED',
    secret: 'SECRET AWARD',
    secretHint: 'KEEP PLAYING TO DISCOVER IT',
    offline: 'OFFLINE · PROGRESS UNAVAILABLE',
    ariaCategory: 'Category',
    category: {
      combate: 'COMBAT',
      score: 'SCORE',
      social: 'SOCIAL',
    },
    rarity: {
      comun: 'COMMON',
      raro: 'RARE',
      epico: 'EPIC',
      legendario: 'LEGENDARY',
    },
    items: {
      'primera-sangre': { name: 'FIRST BLOOD', description: 'WIN YOUR FIRST VERSUS MATCH' },
      'ko-tecnico': { name: 'TECHNICAL K.O.', description: 'WIN 10 MATCHES BY K.O.' },
      veterano: { name: 'VETERAN', description: 'PLAY 100 MATCHES' },
      verdugo: { name: 'EXECUTIONER', description: 'WIN 50 VERSUS MATCHES' },
      imparable: { name: 'UNSTOPPABLE', description: 'WIN 5 MATCHES IN A ROW' },
      artillero: { name: 'GUNNER', description: 'DEAL 100 000 DAMAGE' },
      'sin-un-rasguno': { name: 'WITHOUT A SCRATCH', description: 'WIN A MATCH TAKING NO DAMAGE' },
      'dominio-total': { name: 'TOTAL DOMINANCE', description: 'WIN 10 MATCHES IN A ROW' },
      'leyenda-arena': { name: 'ARENA LEGEND', description: 'WIN 250 VERSUS MATCHES' },
      aprendiz: { name: 'APPRENTICE', description: 'REACH A 512 TILE' },
      maraton: { name: 'MARATHON', description: 'PLAY 50 SOLO MATCHES' },
      maestro: { name: 'MASTER', description: 'REACH A 1024 TILE' },
      'cadena-mortal': { name: 'DEADLY CHAIN', description: 'REACH A x15 COMBO' },
      'seis-cifras': { name: 'SIX FIGURES', description: 'SCORE 100 000 POINTS IN ONE MATCH' },
      'primer-2048': { name: 'FIRST 2048', description: 'REACH YOUR FIRST 2048 TILE' },
      'reaccion-en-cadena': { name: 'CHAIN REACTION', description: 'REACH A x25 COMBO' },
      'medio-millon': { name: 'HALF A MILLION', description: 'ACCUMULATE 500 000 POINTS' },
      'mas-alla': { name: 'BEYOND', description: 'REACH A 4096 TILE' },
      'primer-rival': { name: 'FIRST RIVAL', description: 'FACE ANOTHER PLAYER' },
      sociable: { name: 'SOCIABLE', description: 'FACE 10 DIFFERENT RIVALS' },
      'rival-fiel': { name: 'LOYAL RIVAL', description: 'PLAY 10 MATCHES AGAINST THE SAME RIVAL' },
      'cara-conocida': { name: 'FAMILIAR FACE', description: 'FACE 50 DIFFERENT RIVALS' },
      'david-contra-goliat': {
        name: 'DAVID AND GOLIATH',
        description: 'BEAT A RIVAL 300 ELO POINTS ABOVE YOU',
      },
    },
  },

  settings: {
    title: 'SETTINGS',
    audio: 'AUDIO',
    sound: 'SOUND',
    music: 'MUSIC',
    effects: 'EFFECTS',
    vibration: 'VIBRATION',
    game: 'GAME',
    language: 'LANGUAGE',
    languageName: 'ENGLISH',
    crt: 'CRT EFFECT',
    reducedMotion: 'REDUCE MOTION',
    reducedMotionHint: 'YOUR SYSTEM MAY FORCE THIS',
    save: 'SAVE CHANGES',
    unsavedTitle: 'UNSAVED CHANGES',
    unsavedMessage: 'LEAVING NOW RESTORES\nTHE PREVIOUS VALUES.',
    saveAndExit: 'SAVE AND EXIT',
    discardChanges: 'DISCARD',
    keepEditing: 'KEEP EDITING',
    account: 'ACCOUNT',
    edit: 'EDIT',
    signIn: 'SIGN IN',
    signOut: 'SIGN OUT',
    signingOut: 'SIGNING OUT',
    signedInAs: (username: string) => `SIGNED IN AS ${username}`,
    progressKept: 'YOUR PROGRESS CARRIES OVER TO THE ACCOUNT',
    footer: '2K48 BATTLE v1.0',
    support: 'SUPPORT · PRIVACY',
  },

  auth: {
    signInTitle: 'SIGN IN',
    signingIn: 'SIGNING IN',
    username: 'USERNAME',
    password: 'PASSWORD',
    email: 'EMAIL',
    playerName: 'PLAYER NAME',
    rememberMe: 'REMEMBER ME',
    forgot: 'FORGOT?',
    forgotSoon: 'COMING SOON',
    createAccount: 'CREATE ACCOUNT',
    creating: 'CREATING',
    playAsGuest: 'PLAY AS GUEST',
    insertCoin: 'INSERT COIN · v1.0',
    registerTitle: 'NEW PLAYER',
    chooseAvatar: 'CHOOSE YOUR AVATAR',
    available: '✓ AVAILABLE',
    unavailable: '✕ NOT AVAILABLE',
    termsLine1: 'BY CONTINUING YOU ACCEPT',
    termsLine2: 'THE TERMS OF THE GAME',
    charactersLeft: (n: number) => `${n} MORE CHARACTERS`,
    ariaAvatar: 'Choose your avatar',
    ariaStrength: 'Password strength',
    errors: {
      invalidCredentials: 'WRONG USERNAME OR PASSWORD',
      accountDisabled: 'THIS ACCOUNT IS DISABLED',
      usernameTaken: 'THAT NAME IS ALREADY TAKEN',
      emailTaken: 'THAT EMAIL ALREADY HAS AN ACCOUNT',
      playerAlreadyClaimed: 'THIS DEVICE ALREADY HAS AN ACCOUNT. SIGN IN',
      invalidUsername: 'INVALID NAME',
      invalidEmail: 'INVALID EMAIL',
      invalidPassword: (min: number) => `THE PASSWORD NEEDS ${min} CHARACTERS`,
      deviceRequired: 'COULD NOT IDENTIFY THIS DEVICE',
      unauthorized: 'THE SESSION IS NO LONGER VALID',
      serverError: 'SERVER ERROR. TRY AGAIN',
      generic: 'COULD NOT COMPLETE. TRY AGAIN',
      offline: 'NO CONNECTION TO THE SERVER',
      rateLimitedOneMinute: 'TOO MANY ATTEMPTS. WAIT A MINUTE',
      rateLimited: (minutes: number) => `TOO MANY ATTEMPTS. WAIT ${minutes} MINUTES`,
      usernameRequired: 'TYPE A NAME',
      usernameTooShort: (min: number) => `AT LEAST ${min} CHARACTERS`,
      usernameTooLong: (max: number) => `AT MOST ${max} CHARACTERS`,
      usernameCharset: 'ONLY LETTERS, NUMBERS AND _',
      usernameEdges: 'CANNOT START OR END WITH _',
      emailRequired: 'TYPE YOUR EMAIL',
      emailTooLong: 'EMAIL TOO LONG',
    },
  },

  toasts: {
    settingsSaved: 'SETTINGS SAVED',
    signedIn: 'SIGNED IN',
    signedOut: 'SIGNED OUT',
    accountCreated: 'ACCOUNT CREATED',
    unstableConnection: 'UNSTABLE CONNECTION',
    connectionRestored: 'CONNECTION RESTORED',
    matchCreated: 'MATCH CREATED SUCCESSFULLY',
    codeExpired: 'THE MATCH CODE EXPIRED',
    codeExpiredDetail: 'THE CODE IS NO LONGER VALID OR THE ROOM CLOSED.',
    matchUnavailable: 'MATCH UNAVAILABLE',
    couldNotConnect: 'COULD NOT CONNECT',
    couldNotConnectServer: 'COULD NOT CONNECT TO THE SERVER',
    offlineRun: 'PLAYING OFFLINE · NOT SAVED',
    botFilledIn: 'NOBODY ONLINE · THE CPU STEPS IN',
    opponentLeft: 'YOUR OPPONENT LEFT THE MATCH',
    matchOver: 'MATCH OVER',
    opponentFound: 'RIVAL FOUND',
    achievementsUnlocked: (names: string) => `AWARD: ${names}`,
  },
};
